// src/pages/buyer/InsurancePage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { insuranceApi } from '@/api/services'
import { Shield, Truck, CreditCard, FileText, Loader2, AlertTriangle } from 'lucide-react'
import toast from 'react-hot-toast'

const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`

const typeLabels = { cargo: 'Cargo & Transit', trade_credit: 'Trade Credit' }
const statusColors = {
  active:      'bg-green-100 text-green-700',
  claimed:     'bg-yellow-100 text-yellow-700',
  expired:     'bg-gray-100 text-gray-500',
  cancelled:   'bg-red-100 text-red-600',
  submitted:   'bg-blue-100 text-blue-700',
  under_review:'bg-yellow-100 text-yellow-700',
  approved:    'bg-green-100 text-green-700',
  rejected:    'bg-red-100 text-red-600',
  settled:     'bg-emerald-100 text-emerald-700',
}

const CLAIM_REASONS = [
  { value: 'damage', label: 'Fabric Damaged in Transit' },
  { value: 'loss', label: 'Shipment Lost' },
  { value: 'delay', label: 'Excessive Delay (>14 days)' },
  { value: 'non_payment', label: 'Buyer Non-Payment (Trade Credit)' },
]

export default function InsurancePage() {
  const qc = useQueryClient()
  const [tab, setTab] = useState('policies')
  const [claimForm, setClaimForm] = useState(null) // policy_id when filing

  const { data: policiesData, isLoading } = useQuery({
    queryKey: ['insurance-policies'],
    queryFn: () => insuranceApi.list().then(r => r.data),
  })

  const { data: plansData } = useQuery({
    queryKey: ['insurance-plans'],
    queryFn: () => insuranceApi.plans({ order_value: 10000 }).then(r => r.data),
  })

  const { data: claimsData } = useQuery({
    queryKey: ['insurance-claims'],
    queryFn: () => insuranceApi.claims().then(r => r.data),
    enabled: tab === 'claims',
  })

  const fileClaim = useMutation({
    mutationFn: (d) => insuranceApi.fileClaim(d),
    onSuccess: (res) => {
      toast.success(res.data.message)
      qc.invalidateQueries({ queryKey: ['insurance-claims'] })
      setClaimForm(null)
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to file claim'),
  })

  const policies = policiesData?.data || []
  const plans = plansData?.data || []
  const claims = claimsData?.data || []

  function handleFileClaim(e) {
    e.preventDefault()
    const fd = new FormData(e.target)
    fileClaim.mutate({
      policy_id: claimForm,
      reason: fd.get('reason'),
      description: fd.get('description'),
      claim_amount: Number(fd.get('claim_amount')),
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Shield className="w-6 h-6 text-pink-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fabrinza Insurance</h1>
          <p className="text-xs text-gray-500">Protect your orders with cargo and trade credit insurance</p>
        </div>
      </div>

      {/* Plan cards */}
      <div className="grid md:grid-cols-2 gap-4">
        {plans.map(plan => (
          <div key={plan.key} className="bg-white rounded-xl border p-5">
            <div className="flex items-start gap-3">
              {plan.key === 'cargo'
                ? <Truck className="w-8 h-8 text-blue-500 flex-shrink-0" />
                : <CreditCard className="w-8 h-8 text-purple-500 flex-shrink-0" />}
              <div>
                <h3 className="font-bold text-gray-900">{plan.name}</h3>
                <p className="text-xs text-gray-500 mt-1">{plan.description}</p>
                <div className="flex items-center gap-4 mt-3 text-xs">
                  <span className="text-gray-600">Rate: <strong>{plan.rate}</strong></span>
                  <span className="text-gray-600">Coverage: <strong>{plan.coverage}</strong></span>
                  <span className="text-gray-600">Valid: <strong>{plan.validity_days} days</strong></span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {[
          { key: 'policies', label: 'My Policies', icon: FileText },
          { key: 'claims', label: 'Claims', icon: AlertTriangle },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition ${
              tab === t.key ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Policies tab */}
      {tab === 'policies' && (
        isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : policies.length === 0 ? (
          <div className="text-center py-12">
            <Shield className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-400">No insurance policies yet</p>
            <p className="text-xs text-gray-400 mt-1">Add insurance during checkout or from your order details page</p>
          </div>
        ) : (
          <div className="space-y-3">
            {policies.map(policy => (
              <div key={policy.id} className="bg-white rounded-xl border p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-bold text-gray-900">{policy.policy_number}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${statusColors[policy.status]}`}>
                        {policy.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {typeLabels[policy.type]} · Order #{policy.order?.order_number}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-gray-900">Coverage: {fmt(policy.coverage_amount)}</p>
                    <p className="text-xs text-gray-500">Premium: {fmt(policy.premium_amount)}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t">
                  <span className="text-xs text-gray-400">
                    {policy.start_date} → {policy.end_date}
                  </span>
                  {policy.status === 'active' && (
                    <button onClick={() => setClaimForm(policy.id)}
                      className="text-xs font-medium text-pink-600 hover:text-pink-700">
                      File a Claim
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Claims tab */}
      {tab === 'claims' && (
        claims.length === 0 ? (
          <div className="text-center py-12">
            <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-400">No claims filed</p>
          </div>
        ) : (
          <div className="space-y-3">
            {claims.map(claim => (
              <div key={claim.id} className="bg-white rounded-xl border p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-bold text-gray-900">{claim.claim_number}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${statusColors[claim.status]}`}>
                        {claim.status.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Policy #{claim.policy?.policy_number} · {claim.reason.replace('_', ' ')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-gray-900">Claimed: {fmt(claim.claim_amount)}</p>
                    {claim.settled_amount && (
                      <p className="text-xs text-green-600">Settled: {fmt(claim.settled_amount)}</p>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-2">{claim.description}</p>
              </div>
            ))}
          </div>
        )
      )}

      {/* Claim form modal */}
      {claimForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <form onSubmit={handleFileClaim}
            className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg mx-4 space-y-4">
            <h2 className="font-bold text-lg text-gray-900">File Insurance Claim</h2>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Reason *</label>
              <select name="reason" required className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">Select reason...</option>
                {CLAIM_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Description *</label>
              <textarea name="description" required rows={3}
                placeholder="Describe what happened in detail..."
                className="w-full border rounded-lg px-3 py-2 text-sm resize-none" />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Claim Amount (₹) *</label>
              <input type="number" name="claim_amount" required min="1" step="0.01"
                className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>

            <div className="flex gap-3">
              <button type="submit" disabled={fileClaim.isPending}
                className="px-5 py-2 rounded-lg text-sm font-bold text-white bg-pink-500 hover:bg-pink-600 transition disabled:opacity-50 flex items-center gap-2">
                {fileClaim.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Submit Claim
              </button>
              <button type="button" onClick={() => setClaimForm(null)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
